import type { LucideIcon } from "lucide-react";
import { cn } from "@/lib/utils";

/**
 * Empty-state primitive — v2 (BRANDING.md v1.2 § 6.10).
 *
 * Empty states are first-class — not a fallback. Defaults to Linear-style
 * fills-full-width treatment with brand-aligned chrome. Consumers can override
 * via `className` when the parent is a constrained card (e.g., sidebar widget)
 * and a centered-island variant is genuinely needed.
 *
 * Required structure per spec:
 * - Icon (16–24px lucide-react; semantically appropriate, NOT decorative)
 * - Headline (H3 size; font-bold; declarative)
 * - Description (body small; 1–2 sentences; max-w-prose)
 * - Single primary action (CTA primary)
 *
 * MUST NOT (per spec):
 * - "Nothing to see here" framing — empty states teach.
 * - Multiple CTAs — one primary action only.
 * - Decorative custom-SVG illustrations — use a lucide icon.
 *
 * Source: docs/marketing/BRANDING.md v1.2 § 6.10 (canonical pattern);
 * docs/engineering/contracts/UI_COMPONENT_CONTRACT.md § Token surface
 * "Empty-state pattern". Migration: ENG-DESIGN-EMPTY-STATE-V2 (Wave C · C1
 * of the 2026-05-09 design-system roadmap).
 */

interface EmptyStateProps {
  icon: LucideIcon;
  title: string;
  description: string;
  action?: React.ReactNode;
  className?: string;
}

export default function EmptyState({
  icon: Icon,
  title,
  description,
  action,
  className,
}: EmptyStateProps) {
  return (
    <div
      className={cn(
        "bg-surface-alt flex w-full flex-col items-start gap-4 rounded-2xl p-9 md:p-12",
        className,
      )}
    >
      <Icon className="text-primary size-6" />
      <h3 className="text-ink text-xl font-bold">{title}</h3>
      <p className="text-ink-soft max-w-prose text-sm leading-relaxed">
        {description}
      </p>
      {action && <div>{action}</div>}
    </div>
  );
}
